import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import { getDataWithComp, getAlert } from "../../../../utils/SamuiUtils";
import { SUMMARY_FIELDS, DETAIL_FIELDS } from "./Fields";
import { formatThaiDateUi } from "../../../../utils/DateUtils";

export default function useInactiveCustomers({
  startDate,
  endDate,
  search,
  sortField,
  sortOrder,
  page,
  perPage,
  statusFilter,
}) {
  const [rawData, setRawData] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [detailInfo, setDetailInfo] = useState({
    index: 0,
    code: "",
    name: "",
  });

  useEffect(() => {
    getDataWithComp("API_0702_SO_D", "ORDER BY Doc_Date DESC")
      .then(setRawData)
      .catch((e) => getAlert("FAILED", e.message));
  }, []);

  const byDate = useMemo(
    () =>
      rawData.filter((i) => {
        const d = moment(i.Doc_Date).format("YYYY-MM-DD");
        return d >= startDate && d <= endDate;
      }),
    [rawData, startDate, endDate]
  );

  const grouped = useMemo(() => {
    const m = {};
    byDate.forEach((item) => {
      const key = item.AR_Code;
      if (!key) return;
      const ar = item.AR_Code;
      if (!m[key]) {
        m[key] = {
          AR_Code: ar,
          AR_Name: item.AR_Name,
          Item_Name: item.Item_Name,
          purchaseCount: 0,
          Item_Total: 0,
          _dates: [],
        };
      }
      m[key].purchaseCount += 1;
      m[key].Item_Total += +item.Item_Total || 0;
      m[key]._dates.push(item.Doc_Date);
    });
    return Object.values(m).map((entry) => {
      const maxDate = entry._dates.reduce((max, d) => {
        return moment(d).isAfter(max) ? moment(d) : max;
      }, moment(entry._dates[0]));
      const lastPurchaseDate = formatThaiDateUi(maxDate);
      const daysSinceLast = moment(endDate).diff(maxDate, "days");
      return {
        ...entry,
        lastPurchaseDate,
        daysSinceLast,
        purchaseCount: entry.purchaseCount,
        totalSpent: entry.Item_Total,
      };
    });
  }, [byDate, endDate]);

  const filtered = useMemo(() => {
    let a = grouped;
    if (statusFilter) {
      a = a.filter((i) => i.DocStatusPaid_Name === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      a = a.filter((i) =>
        [i.Item_Code, i.Item_Name].join(" ").toLowerCase().includes(q)
      );
    }
    return a;
  }, [grouped, statusFilter, search]);

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const va = a[sortField] ?? "",
        vb = b[sortField] ?? "";
      if (!isNaN(+va) && !isNaN(+vb)) {
        return sortOrder === "asc" ? va - vb : vb - va;
      }
      return sortOrder === "asc"
        ? va.toString().localeCompare(vb)
        : vb.toString().localeCompare(va);
    });
  }, [filtered, sortField, sortOrder]);

  const total = sorted.length;
  const pages = perPage > 0 ? Math.ceil(total / perPage) : 1;
  const current = useMemo(
    () =>
      perPage > 0 ? sorted.slice((page - 1) * perPage, page * perPage) : sorted,
    [sorted, page, perPage]
  );

  const sums = useMemo(() => {
    if (byDate.length === 0) {
      return {
        lastPurchaseDate: null,
        daysSinceLast: null,
        totalOrders: 0,
        totalSpent: 0,
      };
    }
    const globalMax = byDate
      .map((i) => moment(i.Doc_Date))
      .reduce(
        (max, d) => (d.isAfter(max) ? d : max),
        moment(byDate[0].Doc_Date)
      );
    return {
      lastPurchaseDate: globalMax.format("YYYY-MM-DD"),
      daysSinceLast: moment(endDate).diff(globalMax, "days"),
      totalOrders: byDate.length,
      totalSpent: byDate.reduce((s, i) => s + (+i.Item_Total || 0), 0),
    };
  }, [byDate, endDate]);

  const showDetailByIndex = useCallback(
    (idx) => {
      const prod = current[idx];
      if (!prod) return;
      const filter = [
        `AND Item_Code='${prod.Item_Code}'`,
        `AND Doc_Date>='${startDate}'`,
        `AND Doc_Date<='${endDate}'`,
        "ORDER BY Doc_Date",
      ].join(" ");
      getDataWithComp("API_0702_SO_D", filter)
        .then((list) => {
          setDetailData(list || []);
          setDetailInfo({
            index: idx,
            code: prod.Item_Code,
            name: prod.Item_Name,
          });
        })
        .catch((e) => getAlert("FAILED", e.message));
    },
    [current, startDate, endDate]
  );

  return {
    current,
    total,
    pages,
    sums,
    detailData,
    detailInfo,
    showDetailByIndex,
  };
}
